import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2, Save, X, Info, Pencil, Eye, EyeOff } from 'lucide-react';
import { incentiveAPI } from '../../services/api';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import Modal from '../../components/Modal';
import { FormErrorBanner } from '../../components/FormErrorBanner';
import {
  coordKey, formatMoney, formatNumber, isPerUnitPlan, MATCH_OP_LABELS, VALUELESS_OPS, RULE_TYPE_LABELS,
  type IncentivePlan, type CoordinateOption, type MultiplierField, type SalesField,
  type IncentiveRuleType, type ProductCoordinate, type Scope,
} from './types';
import { perUnitExamples, perUnitPay, validatePerUnit, type PerUnitSettings } from './perUnit';

/**
 * One plan: the grid, who it covers, and the rules that decide who qualifies.
 *
 * ── THE GRID IS THE POINT ────────────────────────────────────────────────────
 * It is laid out exactly like the sheet an admin already has — products across
 * the top, achievement bands down the side, an amount in each cell, with the
 * weightage and max potential rows above. That is not decoration: the whole
 * reason this module can replace a spreadsheet is that the spreadsheet is
 * recognisable in it, and an admin can check a cell against their own file
 * without translating between two shapes.
 *
 * Bands are ROWS shared by every product. The per-product difference is the
 * AMOUNT — which is how one product pays from 25% while another pays nothing
 * until 80%, exactly as on the reference sheet.
 *
 * ── SAVED AS A UNIT ──────────────────────────────────────────────────────────
 * The whole grid goes in one call. Adding a band, retyping three cells and
 * dropping a product is one edit in the admin's head, and saving it piecewise
 * could leave a half-applied ladder that pays real money.
 *
 * ── A PER-UNIT PLAN HAS NO GRID ──────────────────────────────────────────────
 * It pays a rate for every unit sold, all counted products together, so in place
 * of the grid it shows which products count, the "pays from" figure and the rate
 * ladder, with worked examples computed as the admin types. It is saved as a unit
 * too, and checked first with the server's own words so a bad ladder is caught
 * before anything is written. Its sale rules decide which units are counted; it
 * has no target, so a minimum-achievement rule is never offered.
 */

/** What a product the catalogue no longer lists is called, rather than nothing. */
const UNLISTED_PRODUCT = 'Product no longer listed';

/** The coordinate a `coordKey` was made from ("product::size", size blank for a whole product). */
const coordFromKey = (k: string): ProductCoordinate => {
  const [productId, skuId] = k.split('::');
  return { productId, skuId: skuId || null };
};

interface Props {
  projectId: number;
  plan: IncentivePlan;
  coordinates: CoordinateOption[];
  salesFields: SalesField[];
  storeFields: MultiplierField[];
  employeeFields: MultiplierField[];
  onSaved: () => Promise<void> | void;
}

interface DraftBand {
  id: string | null;
  label: string;
  fromPct: string;
  toPct: string;
}

interface DraftRate {
  fromUnit: string;
  rate: string;
}

/** A typed box as a number; blank is NaN so the checks refuse it rather than read 0. */
const typed = (v: string): number => (v.trim() === '' ? Number.NaN : Number(v));

/** The ladder from the reference sheet, offered when a plan has no bands yet. */
const STARTER_BANDS: DraftBand[] = [
  { id: null, label: '0-24%', fromPct: '0', toPct: '25' },
  { id: null, label: '25-49%', fromPct: '25', toPct: '50' },
  { id: null, label: '50-74%', fromPct: '50', toPct: '75' },
  { id: null, label: '75-79%', fromPct: '75', toPct: '80' },
  { id: null, label: '80-89%', fromPct: '80', toPct: '90' },
  { id: null, label: '90%-99%', fromPct: '90', toPct: '100' },
  { id: null, label: '100%-109%', fromPct: '100', toPct: '110' },
  { id: null, label: '110%-119%', fromPct: '110', toPct: '120' },
  { id: null, label: '>=120%', fromPct: '120', toPct: '' },
];

export default function IncentivePlanEditor({
  projectId, plan, coordinates, salesFields, storeFields, employeeFields, onSaved,
}: Props) {
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description ?? '');
  // Not edited here — a plan is switched on or off from its row in the list, so
  // this only carries the current setting through a save.
  const active = plan.active;
  const [scopeType, setScopeType] = useState<Scope>(plan.scopeType);
  const [fieldKey, setFieldKey] = useState(
    plan.fieldSource && plan.fieldKey ? `${plan.fieldSource}::${plan.fieldKey}` : '::',
  );
  const [matchOp, setMatchOp] = useState(plan.matchOp ?? 'eq');
  const [matchValue, setMatchValue] = useState(
    Array.isArray(plan.matchValue) ? plan.matchValue.join(', ') : String(plan.matchValue ?? ''),
  );
  const [newJoinerMonths, setNewJoinerMonths] = useState(plan.newJoinerMonths?.toString() ?? '');
  const [newJoinerAmount, setNewJoinerAmount] = useState(plan.newJoinerAmount?.toString() ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(plan.effectiveFrom?.slice(0, 7) ?? '');
  const [effectiveTo, setEffectiveTo] = useState(plan.effectiveTo?.slice(0, 7) ?? '');

  const isPerUnit = isPerUnitPlan(plan);

  const [bands, setBands] = useState<DraftBand[]>(
    plan.bands.length > 0
      ? plan.bands.map((b) => ({
        id: b.id, label: b.label, fromPct: String(b.fromPct),
        toPct: b.toPct === null ? '' : String(b.toPct),
      }))
      : isPerUnit ? [] : STARTER_BANDS,
  );
  // The plan's products: the grid's columns, or — on a per-unit plan — the
  // products ticked as counted when "All products" is off.
  const [productKeys, setProductKeys] = useState<string[]>(
    plan.products.length > 0
      ? plan.products.map((p) => coordKey(p.coord))
      : isPerUnit ? [] : coordinates.slice(0, 5).map((c) => coordKey(c.coord)),
  );
  const [weightage, setWeightage] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    plan.products.forEach((p) => { m[coordKey(p.coord)] = String(p.weightagePct); });
    return m;
  });
  const [maxPotential, setMaxPotential] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    plan.products.forEach((p) => { m[coordKey(p.coord)] = String(p.maxPotential); });
    return m;
  });
  // cell key = `${bandIndex}::${coordKey}`
  const [cells, setCells] = useState<Record<string, string>>(() => {
    const byBandId = new Map(plan.bands.map((b, i) => [b.id, i]));
    const m: Record<string, string> = {};
    plan.amounts.forEach((a) => {
      const idx = byBandId.get(a.bandId);
      if (idx === undefined) return;
      m[`${idx}::${a.productId}::${a.skuId ?? ''}`] = String(a.amount);
    });
    return m;
  });

  // Per-unit plans only.
  const [allProducts, setAllProducts] = useState(plan.unitAllProducts ?? plan.products.length === 0);
  const [minUnits, setMinUnits] = useState(plan.unitMinUnits?.toString() ?? '');
  const [rates, setRates] = useState<DraftRate[]>(
    plan.unitRates && plan.unitRates.length > 0
      ? plan.unitRates.map((r) => ({ fromUnit: String(r.fromUnit), rate: String(r.rate) }))
      : [{ fromUnit: '1', rate: '' }],
  );
  // Set when Save was refused on the check below; the message then follows the
  // draft, so fixing the ladder clears it without another click.
  const [checkFailed, setCheckFailed] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [ruleDraft, setRuleDraft] = useState<null | {
    id: string | null; ruleType: IncentiveRuleType; coordKeyValue: string;
    fromFieldId: string; toFieldId: string; minDays: string; maxDays: string;
    fieldId: string; matchOp: string; matchValue: string; minAchievementPct: string;
  }>(null);
  const [ruleError, setRuleError] = useState<string | null>(null);

  useEffect(() => { setNotice(null); }, [bands, cells, productKeys, allProducts, minUnits, rates]);

  const fields = scopeType === 'store' ? storeFields : employeeFields;
  const coordByKey = useMemo(
    () => new Map(coordinates.map((c) => [coordKey(c.coord), c])),
    [coordinates],
  );
  const dateFields = useMemo(() => salesFields.filter((f) => f.fieldType === 'date'), [salesFields]);

  // A counted product the project no longer lists keeps its place: it is read off
  // the plan, so saving does not quietly drop it.
  const planProductByKey = useMemo(
    () => new Map(plan.products.map((p) => [coordKey(p.coord), p])),
    [plan.products],
  );
  const labelFor = (k: string): string =>
    coordByKey.get(k)?.label || planProductByKey.get(k)?.label || UNLISTED_PRODUCT;
  /** The per-unit checklist: the project's products, then any the plan counts that it no longer lists. */
  const countable = useMemo(() => [
    ...coordinates.map((c) => ({ key: coordKey(c.coord), label: c.label })),
    ...plan.products
      .filter((p) => !coordByKey.has(coordKey(p.coord)))
      .map((p) => ({ key: coordKey(p.coord), label: p.label || UNLISTED_PRODUCT })),
  ], [coordinates, plan.products, coordByKey]);

  /** Exactly what savePerUnit sends, rates in order. */
  const perUnitSettings = useMemo((): PerUnitSettings => ({
    minUnits: minUnits.trim() === '' ? null : Number(minUnits),
    allProducts,
    products: allProducts ? [] : productKeys
      .map((k) => coordByKey.get(k)?.coord ?? planProductByKey.get(k)?.coord ?? null)
      .filter((c): c is ProductCoordinate => c !== null)
      .map((c) => ({ productId: c.productId, skuId: c.skuId })),
    rates: rates
      .map((r) => ({ fromUnit: typed(r.fromUnit), rate: typed(r.rate) }))
      .sort((a, b) => a.fromUnit - b.fromUnit),
  }), [minUnits, allProducts, productKeys, rates, coordByKey, planProductByKey]);
  const perUnitProblem = isPerUnit ? validatePerUnit(perUnitSettings) : null;

  /** "22 → ₹1,100" for the counts either side of each step — only once the ladder itself is sound. */
  const examples = useMemo(() => {
    if (!isPerUnit || validatePerUnit({ ...perUnitSettings, allProducts: true }) !== null) return [];
    return perUnitExamples(perUnitSettings.minUnits, perUnitSettings.rates).map((units) => ({
      units, amount: perUnitPay(perUnitSettings.minUnits, perUnitSettings.rates, units).amount,
    }));
  }, [isPerUnit, perUnitSettings]);

  /** A rate's last unit: one before the next start above it, or open on the top step. */
  const rateTo = (i: number): string => {
    const from = typed(rates[i].fromUnit);
    if (!Number.isFinite(from)) return '—';
    const higher = rates.map((r) => typed(r.fromUnit)).filter((n) => Number.isFinite(n) && n > from);
    return higher.length === 0 ? 'and above' : `to ${Math.min(...higher) - 1}`;
  };

  // What a rule may be about. A per-unit plan has no target, so no minimum
  // achievement, and its rules can name only the products it counts.
  const ruleTypes = (Object.keys(RULE_TYPE_LABELS) as IncentiveRuleType[])
    .filter((t) => !isPerUnit || t !== 'min_achievement');
  const ruleProductKeys = isPerUnit && allProducts ? countable.map((c) => c.key) : productKeys;

  const setCell = (bandIndex: number, key: string, value: string) =>
    setCells((prior) => ({ ...prior, [`${bandIndex}::${key}`]: value }));
  const cellValue = (bandIndex: number, key: string) => cells[`${bandIndex}::${key}`] ?? '';

  /** Row total — what somebody earns if EVERY product lands in this band. */
  const rowTotal = (bandIndex: number): number =>
    productKeys.reduce((n, k) => n + (Number(cellValue(bandIndex, k)) || 0), 0);

  const saveAll = async () => {
    if (perUnitProblem) {
      setError(null);
      setNotice(null);
      setCheckFailed(true);
      return;
    }
    setCheckFailed(false);
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const [source, ...rest] = fieldKey.split('::');
      const key = rest.join('::');
      const hasCondition = source !== '' && key !== '';
      await incentiveAPI.updatePlan(projectId, plan.id, {
        name, description, scopeType, active,
        fieldSource: hasCondition ? source : null,
        fieldKey: hasCondition ? key : null,
        matchOp: hasCondition ? matchOp : null,
        matchValue: hasCondition && !VALUELESS_OPS.has(matchOp)
          ? (matchOp === 'in' || matchOp === 'not_in' || matchOp === 'between'
            ? matchValue.split(',').map((v) => v.trim()).filter(Boolean)
            : matchValue)
          : null,
        newJoinerMonths: newJoinerMonths === '' ? null : Number(newJoinerMonths),
        newJoinerAmount: newJoinerAmount === '' ? null : Number(newJoinerAmount),
        effectiveFrom: effectiveFrom || null,
        effectiveTo: effectiveTo || null,
      });

      // A per-unit plan has no grid to send; the grid call would refuse it.
      if (isPerUnit) {
        await incentiveAPI.savePerUnit(projectId, plan.id, perUnitSettings);
        setNotice('Plan saved.');
        await onSaved();
        return;
      }

      await incentiveAPI.saveGrid(projectId, plan.id, {
        bands: bands.map((b) => ({
          id: b.id,
          label: b.label.trim() || (b.toPct === '' ? `${b.fromPct}%+` : `${b.fromPct}-${b.toPct}%`),
          fromPct: Number(b.fromPct),
          toPct: b.toPct === '' ? null : Number(b.toPct),
        })),
        products: productKeys.map((k) => {
          const c = coordByKey.get(k);
          return {
            productId: c?.coord.productId ?? '',
            skuId: c?.coord.skuId ?? null,
            weightagePct: Number(weightage[k] ?? 0) || 0,
            maxPotential: Number(maxPotential[k] ?? 0) || 0,
          };
        }).filter((p) => p.productId),
        amounts: bands.flatMap((_b, bandIndex) => productKeys.map((k) => {
          const c = coordByKey.get(k);
          return {
            bandIndex,
            productId: c?.coord.productId ?? '',
            skuId: c?.coord.skuId ?? null,
            amount: Number(cellValue(bandIndex, k)) || 0,
          };
        })).filter((a) => a.productId && a.amount > 0),
      });
      setNotice('Plan saved.');
      await onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save the plan.');
    } finally {
      setSaving(false);
    }
  };

  const saveRule = async () => {
    if (!ruleDraft) return;
    setRuleError(null);
    try {
      // A product the catalogue no longer lists is still a product: read it off the
      // plan, so a rule aimed at it is never saved as a rule for every product.
      const k = ruleDraft.coordKeyValue;
      const coord = k ? (coordByKey.get(k)?.coord ?? planProductByKey.get(k)?.coord ?? coordFromKey(k)) : null;
      const body: Record<string, unknown> = {
        ruleType: ruleDraft.ruleType,
        productId: coord?.productId ?? null,
        skuId: coord?.skuId ?? null,
      };
      if (ruleDraft.ruleType === 'date_gap') {
        body.fromFieldId = ruleDraft.fromFieldId;
        body.toFieldId = ruleDraft.toFieldId;
        body.minDays = ruleDraft.minDays === '' ? null : Number(ruleDraft.minDays);
        body.maxDays = ruleDraft.maxDays === '' ? null : Number(ruleDraft.maxDays);
      } else if (ruleDraft.ruleType === 'field_match') {
        body.fieldId = ruleDraft.fieldId;
        body.matchOp = ruleDraft.matchOp;
        body.matchValue = VALUELESS_OPS.has(ruleDraft.matchOp) ? null
          : (ruleDraft.matchOp === 'in' || ruleDraft.matchOp === 'not_in'
            ? ruleDraft.matchValue.split(',').map((v) => v.trim()).filter(Boolean)
            : ruleDraft.matchValue);
      } else {
        body.minAchievementPct = Number(ruleDraft.minAchievementPct);
      }
      if (ruleDraft.id) await incentiveAPI.updateRule(projectId, ruleDraft.id, body);
      else await incentiveAPI.createRule(projectId, plan.id, body);
      setRuleDraft(null);
      await onSaved();
    } catch (err: any) {
      setRuleError(err.response?.data?.message || 'Could not save the rule.');
    }
  };

  const toggleRule = async (ruleId: string, active: boolean) => {
    setError(null);
    try {
      await incentiveAPI.setRuleActive(projectId, ruleId, active);
      await onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not change the rule.');
    }
  };

  const removeRule = async (ruleId: string) => {
    setError(null);
    try {
      await incentiveAPI.deleteRule(projectId, ruleId);
      await onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete the rule.');
    }
  };

  const describeRule = (r: IncentivePlan['rules'][number]): string => {
    const scope = r.coord
      ? coordByKey.get(coordKey(r.coord))?.label ?? 'one product'
      : 'every product';
    if (r.ruleType === 'date_gap') {
      const from = salesFields.find((f) => f.id === r.fromFieldId)?.label ?? 'the first date';
      const to = salesFields.find((f) => f.id === r.toFieldId)?.label ?? 'the second date';
      const bounds = [
        r.minDays !== null ? `at least ${r.minDays}` : null,
        r.maxDays !== null ? `at most ${r.maxDays}` : null,
      ].filter(Boolean).join(' and ');
      return `A sale counts only when ${from} to ${to} is ${bounds} day(s) — ${scope}.`;
    }
    if (r.ruleType === 'field_match') {
      const f = salesFields.find((x) => x.id === r.fieldId)?.label ?? 'a sales detail';
      const v = Array.isArray(r.matchValue) ? r.matchValue.join(', ') : String(r.matchValue ?? '');
      return `A sale counts only when ${f} ${MATCH_OP_LABELS[r.matchOp ?? ''] ?? r.matchOp} ${v} — ${scope}.`;
    }
    // A minimum reads the share of sales that passed a sale rule wherever one applies
    // to the product, and achievement of target where none does — see
    // isCaseFilter in the API's incentive.calc.ts.
    const saleRules = plan.rules.filter((x) => x.active
      && (x.ruleType === 'date_gap' || x.ruleType === 'field_match'));
    const covers = (x: IncentivePlan['rules'][number]) => x.coord === null
      || (r.coord !== null && coordKey(x.coord) === coordKey(r.coord));
    const onPassRate = `Pays nothing unless at least ${r.minAchievementPct}% of sales pass the sale rules`;
    const onAchievement = `Pays nothing below ${r.minAchievementPct}% achievement`;
    if (saleRules.length === 0) return `${onAchievement} — ${scope}.`;
    if (r.coord !== null || saleRules.some((x) => x.coord === null)) {
      return saleRules.some(covers) ? `${onPassRate} — ${scope}.` : `${onAchievement} — ${scope}.`;
    }
    // Every product, but a sale rule covers only some of them.
    return `${onPassRate} for products with a sale rule; otherwise ${onAchievement.charAt(0).toLowerCase()}`
      + `${onAchievement.slice(1)} — ${scope}.`;
  };

  return (
    <div className="space-y-5">
      {checkFailed && perUnitProblem
        ? <PageErrorBanner message={perUnitProblem} />
        : error && <PageErrorBanner message={error} />}
      {notice && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}

      {/* ── Who and when ─────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ip-name">Plan name</label>
            <input
              id="ip-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Non-Mac Selling Stores"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ip-desc">Description</label>
            <input
              id="ip-desc" type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-gray-200 p-3">
          <p className="text-sm font-medium text-gray-900">Who this plan covers</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Leave the field blank for the catch-all. Plans are tried in order and the first match
            wins, so put the specific ones first.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
            <select
              value={scopeType}
              onChange={(e) => { setScopeType(e.target.value as Scope); setFieldKey('::'); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="store">By store field</option>
              <option value="employee">By employee field</option>
            </select>
            <select
              value={fieldKey}
              onChange={(e) => { setFieldKey(e.target.value); setMatchValue(''); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="::">Everyone (catch-all)</option>
              {fields.map((f) => (
                <option key={`${f.source}::${f.key}`} value={`${f.source}::${f.key}`}>{f.label}</option>
              ))}
            </select>
            <select
              value={matchOp}
              disabled={fieldKey === '::'}
              onChange={(e) => setMatchOp(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
            >
              {Object.entries(MATCH_OP_LABELS).map(([op, label]) => (
                <option key={op} value={op}>{label}</option>
              ))}
            </select>
            <input
              type="text" value={matchValue} placeholder="Value"
              disabled={fieldKey === '::' || VALUELESS_OPS.has(matchOp)}
              onChange={(e) => setMatchValue(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-900">New joiner guarantee</p>
            <p className="mt-0.5 text-xs text-gray-500">
              Somebody inside this window is assessed on the {isPerUnit ? 'plan' : 'grid'} like everyone else. If they earn
              less than the guarantee, they are paid the guarantee, and the difference is shown
              separately as a top-up. If they earn more, they are paid what they earned. Somebody who
              leaves in that month is not paid the guarantee. Leave both blank to switch it off.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Joined under</span>
              <input
                type="number" min={0} value={newJoinerMonths} placeholder="Months"
                onChange={(e) => setNewJoinerMonths(e.target.value)}
                className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
              <span className="text-xs text-gray-500">months → at least ₹</span>
              <input
                type="number" min={0} value={newJoinerAmount} placeholder="Amount"
                onChange={(e) => setNewJoinerAmount(e.target.value)}
                className="w-28 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            {/* Worked through with the figure typed in, so the rule is checked
                against a number rather than read as prose. */}
            {newJoinerAmount !== '' && Number(newJoinerAmount) > 0 && (() => {
              const g = Number(newJoinerAmount);
              const lo = Math.round(g * 0.66);
              const hi = Math.round(g * 1.3);
              const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;
              return (
                <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                  <li>Earns {inr(lo)} → paid {inr(g)}, a top-up of {inr(g - lo)}</li>
                  <li>Earns {inr(hi)} → paid {inr(hi)}, nothing topped up</li>
                </ul>
              );
            })()}
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-900">Live between</p>
            <p className="mt-0.5 text-xs text-gray-500">
              Blank either side means open-ended, so a scheme that changes in April does not rewrite
              what March paid.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <input type="month" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
              <span className="text-xs text-gray-500">→</span>
              <input type="month" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* ── The grid ─────────────────────────────────────────────────────── */}
      {!isPerUnit && (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">The payout grid</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Each product achieves into its own band and the amounts add up. The Total column is
              what somebody would earn if every product landed in that row — nobody is paid it
              directly.
            </p>
          </div>
          <select
            value=""
            onChange={(e) => {
              if (e.target.value && !productKeys.includes(e.target.value)) {
                setProductKeys([...productKeys, e.target.value]);
              }
            }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">Add a product column…</option>
            {coordinates.filter((c) => !productKeys.includes(coordKey(c.coord))).map((c) => (
              <option key={coordKey(c.coord)} value={coordKey(c.coord)}>{c.label}</option>
            ))}
          </select>
        </div>

        {productKeys.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-gray-500">
            Add a product column to start building the grid.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left">Achievement</th>
                  {productKeys.map((k) => (
                    <th key={k} className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {coordByKey.get(k)?.label ?? k}
                        <button
                          type="button"
                          title="Remove this column"
                          onClick={() => setProductKeys(productKeys.filter((x) => x !== k))}
                          className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className=" text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="bg-gray-50/60">
                  <td className="sticky left-0 z-10 bg-gray-50/60 px-3 py-1.5 font-medium text-gray-700">Weightage %</td>
                  {productKeys.map((k) => (
                    <td key={k} className="px-3 py-1.5 text-right">
                      <input
                        type="number" min={0} step="any" value={weightage[k] ?? ''}
                        onChange={(e) => setWeightage({ ...weightage, [k]: e.target.value })}
                        className="w-20 rounded border border-gray-200 px-2 py-0.5 text-right text-xs"
                      />
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right text-xs text-gray-600">
                    {productKeys.reduce((n, k) => n + (Number(weightage[k]) || 0), 0)}%
                  </td>
                  <td />
                </tr>
                <tr className="bg-gray-50/60">
                  <td className="sticky left-0 z-10 bg-gray-50/60 px-3 py-1.5 font-medium text-gray-700">Max potential</td>
                  {productKeys.map((k) => (
                    <td key={k} className="px-3 py-1.5 text-right">
                      <input
                        type="number" min={0} step="any" value={maxPotential[k] ?? ''}
                        onChange={(e) => setMaxPotential({ ...maxPotential, [k]: e.target.value })}
                        className="w-24 rounded border border-gray-200 px-2 py-0.5 text-right text-xs"
                      />
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right text-xs text-gray-600">
                    {formatMoney(productKeys.reduce((n, k) => n + (Number(maxPotential[k]) || 0), 0))}
                  </td>
                  <td />
                </tr>

                {bands.map((band, i) => (
                  <tr key={`${band.id ?? 'new'}-${i}`} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min={0} step="any" value={band.fromPct}
                          onChange={(e) => {
                            const next = [...bands];
                            next[i] = { ...band, fromPct: e.target.value };
                            setBands(next);
                          }}
                          className="w-16 rounded border border-gray-200 px-1.5 py-0.5 text-right text-xs"
                        />
                        <span className="text-xs text-gray-400">to</span>
                        <input
                          type="number" min={0} step="any" value={band.toPct} placeholder="∞"
                          onChange={(e) => {
                            const next = [...bands];
                            next[i] = { ...band, toPct: e.target.value };
                            setBands(next);
                          }}
                          className="w-16 rounded border border-gray-200 px-1.5 py-0.5 text-right text-xs"
                        />
                        <input
                          type="text" value={band.label} placeholder="Label"
                          onChange={(e) => {
                            const next = [...bands];
                            next[i] = { ...band, label: e.target.value };
                            setBands(next);
                          }}
                          className="w-24 rounded border border-gray-200 px-1.5 py-0.5 text-xs"
                        />
                      </div>
                    </td>
                    {productKeys.map((k) => (
                      <td key={k} className="px-3 py-1.5 text-right">
                        <input
                          type="number" min={0} step="any" placeholder="–"
                          value={cellValue(i, k)}
                          onChange={(e) => setCell(i, k, e.target.value)}
                          className="w-24 rounded border border-gray-200 px-2 py-0.5 text-right text-sm"
                        />
                      </td>
                    ))}
                    <td className="px-3 py-1.5 text-right text-sm font-medium text-gray-700">
                      {rowTotal(i) > 0 ? formatMoney(rowTotal(i)) : '–'}
                    </td>
                    <td className="px-2"><TableActionGroup>
                      <TableActionButton action="remove" label="Remove this band"
                        type="button"
                        title="Remove this band"
                        onClick={() => {
                          setBands(bands.filter((_, x) => x !== i));
                          // Cells are addressed by band index, so removing a row
                          // has to shift every cell below it or the amounts move
                          // to the wrong band.
                          setCells((prior) => {
                            const next: Record<string, string> = {};
                            Object.entries(prior).forEach(([key, v]) => {
                              const [idxText, ...rest] = key.split('::');
                              const idx = Number(idxText);
                              if (idx === i) return;
                              next[`${idx > i ? idx - 1 : idx}::${rest.join('::')}`] = v;
                            });
                            return next;
                          });
                        }} />
                    </TableActionGroup></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={() => setBands([...bands, { id: null, label: '', fromPct: '0', toPct: '' }])}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Add a band
          </button>
          <p className="text-xs text-gray-500">
            A band runs from its first figure up to <em>but not including</em> the second. Leave the
            second blank on the top row for &ldquo;and above&rdquo;.
          </p>
        </div>
      </div>
      )}

      {/* ── Pay per unit ─────────────────────────────────────────────────── */}
      {isPerUnit && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Pay per unit</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Units of the counted products add up. Each unit pays the rate of its step.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-900">Products counted</p>
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={allProducts}
                  onChange={(e) => setAllProducts(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                All products
              </label>
              <p className="mt-0.5 text-xs text-gray-500">
                {allProducts
                  ? 'Includes products added later.'
                  : `${countable.filter((c) => productKeys.includes(c.key)).length} of ${countable.length} products`}
              </p>
              {!allProducts && (
                <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-lg border border-gray-100 p-2">
                  {countable.length === 0 && (
                    <p className="text-xs text-gray-500">This project has no products yet.</p>
                  )}
                  {countable.map(({ key: k, label }) => (
                    <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={productKeys.includes(k)}
                        onChange={(e) => setProductKeys(e.target.checked
                          ? [...productKeys, k]
                          : productKeys.filter((x) => x !== k))}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <label className="text-sm font-medium text-gray-900" htmlFor="ip-min-units">Pays from</label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  id="ip-min-units" type="number" min={1} step={1} value={minUnits} placeholder="1"
                  onChange={(e) => setMinUnits(e.target.value)}
                  className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                />
                <span className="text-xs text-gray-500">units</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">Sells fewer → ₹0. Blank = from the first unit.</p>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-900">Rates</p>
              <table className="mt-2 w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="py-1 pr-2 text-left">From unit</th>
                    <th className="py-1 pr-2 text-left">To</th>
                    <th className="py-1 pr-2 text-right">₹ per unit</th>
                    <th className="py-1 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r, i) => (
                    <tr key={i}>
                      <td className="py-1 pr-2">
                        <input
                          type="number" min={1} step={1} value={r.fromUnit}
                          aria-label={`Rate ${i + 1}: from unit`}
                          onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, fromUnit: e.target.value } : x)))}
                          className="w-20 rounded border border-gray-200 px-2 py-0.5 text-right text-sm"
                        />
                      </td>
                      <td className="whitespace-nowrap py-1 pr-2 text-xs text-gray-500">{rateTo(i)}</td>
                      <td className="py-1 pr-2 text-right">
                        <input
                          type="number" min={0} step="0.01" value={r.rate} placeholder="₹"
                          aria-label={`Rate ${i + 1}: ₹ per unit`}
                          onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))}
                          className="w-24 rounded border border-gray-200 px-2 py-0.5 text-right text-sm"
                        />
                      </td>
                      <td className="py-1 text-center"><TableActionGroup>
                        <TableActionButton action="remove" label="Remove this rate"
                          type="button"
                          title="Remove this rate"
                          onClick={() => setRates(rates.filter((_, j) => j !== i))} />
                      </TableActionGroup></td>
                    </tr>
                  ))}
                  {rates.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-3 text-center text-xs text-gray-500">No rates yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <button
                type="button"
                onClick={() => setRates([...rates, { fromUnit: '', rate: '' }])}
                className="mt-2 flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" />
                Add a rate
              </button>
              <p className="mt-1 text-xs text-gray-500">The first starts at unit 1; the last has no cap.</p>
            </div>
          </div>

          {/* Worked through with the figures typed in, so the ladder is checked
              against numbers rather than read as prose. */}
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-4 py-3">
            <span className="text-xs font-medium text-gray-600">What it pays</span>
            {examples.length === 0 ? (
              <span className="text-xs text-gray-400">Set the rates to see examples.</span>
            ) : examples.map((x) => (
              <span
                key={x.units}
                className={`rounded-full px-2 py-0.5 text-xs ${
                  x.amount > 0 ? 'bg-primary-50 font-medium text-primary-800' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {formatNumber(x.units)} → {formatMoney(x.amount)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Rules ────────────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Who qualifies</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {isPerUnit
                ? 'Sale rules decide which units are counted.'
                : 'The slab comes from every sale against target. Rules about individual sales decide '
                  + 'which sales pass; a minimum rule then pays nothing unless enough of them did.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setRuleError(null);
              setRuleDraft({
                id: null,
                // A per-unit plan starts on a date rule when the sales carry two dates
                // to compare, and on a sales-detail rule when they do not.
                ruleType: !isPerUnit ? 'min_achievement' : dateFields.length >= 2 ? 'date_gap' : 'field_match',
                coordKeyValue: '',
                fromFieldId: dateFields[0]?.id ?? '', toFieldId: dateFields[1]?.id ?? '',
                minDays: '0', maxDays: '7',
                fieldId: salesFields[0]?.id ?? '', matchOp: 'eq', matchValue: '',
                minAchievementPct: '90',
              });
            }}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Add a rule
          </button>
        </div>
        {plan.rules.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">
            {isPerUnit
              ? 'No rules — every unit sold is counted.'
              : 'No rules — everything sold counts, and any achievement that reaches a band pays it.'}
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* All-off is worth saying out loud: the plan behaves as if it had no
                rules at all, which looks identical to never having set any. */}
            {plan.rules.every((r) => !r.active) && (
              <p className="bg-amber-50 px-4 py-2 text-xs text-amber-800">
                Every rule here is switched off, so this plan currently behaves as if it had none —
                everything sold counts.
              </p>
            )}
            {plan.rules.map((r) => (
              <div
                key={r.id}
                className={`flex items-start justify-between gap-3 px-4 py-2.5 ${r.active ? '' : 'bg-gray-50'}`}
              >
                <div className="min-w-0">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {RULE_TYPE_LABELS[r.ruleType]}
                  </span>
                  {!r.active && (
                    <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                      Off
                    </span>
                  )}
                  <p className={`mt-1 text-sm ${r.active ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                    {describeRule(r)}
                  </p>
                  {isPerUnit && r.ruleType === 'min_achievement' && (
                    <p className="mt-0.5 text-xs text-gray-500">Not used by per-unit plans</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    type="button"
                    title={r.active ? 'Switch this rule off' : 'Switch this rule back on'}
                    onClick={() => toggleRule(r.id, !r.active)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    {r.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  {/* Nothing it could be edited into would apply, so it can only be
                      switched off or deleted. */}
                  {!(isPerUnit && r.ruleType === 'min_achievement') && (
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => {
                      setRuleError(null);
                      setRuleDraft({
                        id: r.id,
                        ruleType: r.ruleType,
                        coordKeyValue: r.coord ? coordKey(r.coord) : '',
                        fromFieldId: r.fromFieldId ?? '',
                        toFieldId: r.toFieldId ?? '',
                        minDays: r.minDays === null ? '' : String(r.minDays),
                        maxDays: r.maxDays === null ? '' : String(r.maxDays),
                        fieldId: r.fieldId ?? '',
                        matchOp: r.matchOp ?? 'eq',
                        matchValue: Array.isArray(r.matchValue)
                          ? r.matchValue.join(', ') : String(r.matchValue ?? ''),
                        minAchievementPct: r.minAchievementPct === null
                          ? '' : String(r.minAchievementPct),
                      });
                    }}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => removeRule(r.id)}
                    className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {salesFields.length === 0 && (
          <div className="flex items-start gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-xs text-gray-600">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Rules about individual sales read this project&apos;s sales details. Define them with
              &ldquo;Sales details&rdquo; on the Sales tab first — a date rule needs two date fields.
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        {checkFailed && perUnitProblem && (
          <p className="text-sm text-red-700">{perUnitProblem}</p>
        )}
        <button
          type="button"
          onClick={saveAll}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save the plan
        </button>
      </div>

      <Modal
        isOpen={ruleDraft !== null}
        onClose={() => setRuleDraft(null)}
        title={ruleDraft?.id ? 'Edit rule' : 'Add a rule'}
        size="lg"
        footer={
          <div className="flex w-full justify-end gap-2">
            <button type="button" onClick={() => setRuleDraft(null)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="button" onClick={saveRule}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
              Save
            </button>
          </div>
        }
      >
        {ruleDraft && (
          <div className="space-y-4">
            {ruleError && <FormErrorBanner message={ruleError} />}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ir-type">Kind of rule</label>
                <select
                  id="ir-type" value={ruleDraft.ruleType}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, ruleType: e.target.value as IncentiveRuleType })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {ruleTypes.map((k) => <option key={k} value={k}>{RULE_TYPE_LABELS[k]}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ir-product">Applies to</label>
                <select
                  id="ir-product" value={ruleDraft.coordKeyValue}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, coordKeyValue: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Every product</option>
                  {ruleProductKeys.map((k) => (
                    <option key={k} value={k}>{labelFor(k)}</option>
                  ))}
                </select>
              </div>
            </div>

            {ruleDraft.ruleType === 'date_gap' && (
              <div className="space-y-3">
                <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  Counts only the sales where the days from the first date to the second fall inside
                  the range. A sale with either date missing does not count.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ir-from">Earlier date</label>
                    <select id="ir-from" value={ruleDraft.fromFieldId}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, fromFieldId: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                      <option value="">Choose…</option>
                      {dateFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="ir-to">Later date</label>
                    <select id="ir-to" value={ruleDraft.toFieldId}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, toFieldId: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                      <option value="">Choose…</option>
                      {dateFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Gap of at least</span>
                  <input type="number" value={ruleDraft.minDays}
                    onChange={(e) => setRuleDraft({ ...ruleDraft, minDays: e.target.value })}
                    className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
                  <span className="text-xs text-gray-600">and at most</span>
                  <input type="number" value={ruleDraft.maxDays}
                    onChange={(e) => setRuleDraft({ ...ruleDraft, maxDays: e.target.value })}
                    className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
                  <span className="text-xs text-gray-600">days</span>
                </div>
                {dateFields.length < 2 && (
                  <p className="text-xs text-amber-700">
                    This project has {dateFields.length} date field{dateFields.length === 1 ? '' : 's'} in
                    its sales details. A date rule needs two — add them under &ldquo;Sales details&rdquo;
                    on the Sales tab.
                  </p>
                )}
              </div>
            )}

            {ruleDraft.ruleType === 'field_match' && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <select value={ruleDraft.fieldId}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, fieldId: e.target.value })}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Choose a detail…</option>
                  {salesFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <select value={ruleDraft.matchOp}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, matchOp: e.target.value })}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  {Object.entries(MATCH_OP_LABELS).map(([op, label]) => <option key={op} value={op}>{label}</option>)}
                </select>
                <input type="text" value={ruleDraft.matchValue} placeholder="Value"
                  disabled={VALUELESS_OPS.has(ruleDraft.matchOp)}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, matchValue: e.target.value })}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50" />
              </div>
            )}

            {ruleDraft.ruleType === 'min_achievement' && (
              <div>
                <p className="mb-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  For a product with a sales rule (such as a date gap), this is the share of its
                  sales that pass that rule: 227 of 231 passing is 98.3%. For a product with no
                  sales rule, it is achievement of target: 9 sold against a target of 10 is 90%.
                  Either way, the slab is still picked from every sale against target.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Pays nothing below</span>
                  <input type="number" min={0} step="any" value={ruleDraft.minAchievementPct}
                    onChange={(e) => setRuleDraft({ ...ruleDraft, minAchievementPct: e.target.value })}
                    className="w-24 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
                  <span className="text-xs text-gray-600">%</span>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
